import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
// Request schemas for the site-types feature — a project-owned reference-data
// vocabulary (CONVENTIONS §Entity categories) managed under `reference_data:manage`.
// All routes are project-scoped (`/api/projects/:projectId/site-types/*`); a
// site-type never moves between projects, so there is no platform surface.
//
// `name` .max(32) matches site_types.name varchar(32); CI-unique per project
// (uq_site_types_name_ci). `license_tier_code` is the stable code of a tier of the
// global license_tiers catalogue (a DATA catalogue — a sysadmin adds tiers, ADR-055),
// so the wire carries any code and the SERVER decides: the FK, and the tier must be
// one the project INCLUDES (its project_license_tiers rows). It is chosen at create
// and IMMUTABLE thereafter (absent from the PATCH schema below), so a type's
// downstream licensing tier can't silently change under sites already using it.
export const LicenseTierCodeSchema = z.string().trim().min(1).max(16);
// ── POST /api/projects/:projectId/site-types ────────────────────────────────
// `display_order` is system-managed (appended on create); `is_disabled` defaults
// false and flips via the lifecycle routes — neither is a create input. The tier is
// ASKED, never defaulted: no tier is included in every project.
export const CreateSiteTypeRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    license_tier_code: LicenseTierCodeSchema,
})
    .strict();
// ── the import row — the tier cell names the tier as the export writes it (its NAME,
// `license_tiers.name` varchar(32)) or by its code; the model resolves it among the
// project's included tiers.
export const SiteTypeImportRowSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    license_tier_code: z.string().trim().min(1).max(32),
})
    .strict();
// ── PATCH /api/projects/:projectId/site-types/:siteTypeId ────────────────────
// Only `name` is mutable: `license_tier_code` is immutable post-create (see above),
// `is_disabled` flips via the disable/reactivate routes, `display_order` via the
// (deferred) reorder surface. A single required field — not a sparse patch.
export const UpdateSiteTypeRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(32),
})
    .strict();
// ── GET /api/projects/:projectId/site-types/export — current-view export ─────
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045).
// `status`/`license_tier_code` arrive comma-separated (the list filters); `q` is the
// free-text search; `lang` drives header/sheet localization. Gated on
// `reference_data:manage`, like the list.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportSiteTypesQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    license_tier_code: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
// ── POST /api/projects/:projectId/site-types/reorder ────────────────────────
// The FULL ordered set of the project's site-types; the service renumbers
// display_order 1..N in that order, in one transaction (ADR-046 — reorder is
// "ordered siblings within a scope"; here the project, parent-scoped later).
export const ReorderSiteTypesRequestSchema = z
    .object({ orderedIds: z.array(z.number().int().positive()).min(1).max(500) })
    .strict();
